import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import styles from './TextLink.module.scss';

export type TextLinkTone = 'default' | 'quiet' | 'underlined';

type AnchorProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'className'>;

export type TextLinkProps = AnchorProps & {
    children: ReactNode;
    /** `default` in running text, `quiet` for a name or value that navigates, `underlined`
        for a link that must read as one at rest. */
    tone?: TextLinkTone;
    /** Type and layout; the colour too, for a quiet or underlined link. */
    className?: string;
} & ({ to: string; href?: never } | { href: string; to?: never });

/** A text link with the portal's link tones and focus ring: `to` routes in the app, `href` leaves it. */
export function TextLink(props: TextLinkProps) {
    const { children, tone = 'default', className, to, href, ...rest } = props;
    const linkClass = classNames(styles.link, styles[tone], className);

    if (to !== undefined) {
        return (
            <Link {...rest} to={to} className={linkClass}>
                {children}
            </Link>
        );
    }
    return (
        <a {...rest} href={href} className={linkClass}>
            {children}
        </a>
    );
}
